import React, { useRef, useCallback, useMemo } from "react";

import { List } from "../../Components/common/List/List";
import { ListProvider } from "../../Components/common/List/Providers/List";
import { useAppContext } from "../../AppProvider";
import { useListProps } from "./utilities/list";
import { Modal, ModalBody } from "reactstrap";
import { Link } from "react-router-dom";
import Delete from "./Delete";
import { useTypes } from "../../utilities/types";
import { createModalConfig, ModalConfig } from "../../Models/ModalConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faTrash } from "../../utilities/fontawesomeIcons";
import { Article } from "../../Models/Article";
import { useIndexArticlesContext } from "./providers/Index";
import { DeleteArticleProvider } from "./providers/Delete";
import NoAvailableImage from "../../assets/images/no-image-available.png";

const AnyListProvider = ListProvider as any;
const AnyList = List as any;

export const Articles: React.FC = () => {
  const { dictionary } = useAppContext();
  const listRef = useRef<{ refresh: () => void } | null>(null);
  const listProps = useListProps();
  const { modalConfig, setModalConfig } = useIndexArticlesContext();

  const renderItem = useCallback((item: Article) => {
    return <ListItem item={item} />;
  }, []);

  const closeModal = () => {
    setModalConfig(createModalConfig());
  };

  return (
    <>
      <AnyListProvider path={listProps.path} filters={listProps.filters}>
        <AnyList
          ref={listRef}
          title={listProps.title}
          create={listProps.create}
          banner={listProps.banner}
          massiveActions={listProps.massiveActions}
          renderItem={renderItem}
          pagination={listProps.pagination}
          selectRowsEnabled={listProps.selectRowsEnabled}
          omnisearch={listProps.omnisearch}
        />
      </AnyListProvider>

      <Modal isOpen={modalConfig.isOpen} size={modalConfig.size}>
        <ModalBody className={modalConfig.className || ""}>
          {modalConfig.action === "delete" && (
            <DeleteArticleProvider
              data={modalConfig.data}
              callback={() => {
                listRef.current?.refresh();
                closeModal();
              }}
            >
              <Delete />
            </DeleteArticleProvider>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default Articles;

const ListItem: React.FC<{ item: Article }> = React.memo(({ item }) => {
  const { dictionary, accessTokenPayload } = useAppContext();
  const context = useIndexArticlesContext();
  const { ownership, products, booleanOptions, domains } = useTypes();

  const icons = useMemo(() => {
    return {
      ownership:
        item.ownerId !== accessTokenPayload.owner
          ? ownership.find((o) => o.value === "inherited")
          : ownership.find((o) => o.value === "own"),
      shippingRequired: item.shippingRequired
        ? products.find((o) => o.value === "PHYSICAL")
        : products.find((o) => o.value === "DIGITAL"),
      uniqueBarcodeItem: item.uniqueBarcodeItem
        ? booleanOptions.find((o) => o.value === "TRUE")
        : booleanOptions.find((o) => o.value === "FALSE"),
      domain: item.pub
        ? domains.find((o) => o.value === "PUBLIC")
        : domains.find((o) => o.value === "PRIVATE"),
    };
  }, [item]);

  const openModal = useCallback(
    (
      action: ModalConfig["action"],
      data?: string[],
      size: ModalConfig["size"] = "sm",
      className = "",
      title = ""
    ) => {
      context?.setModalConfig({
        isOpen: true,
        action,
        size,
        data,
        className,
        title,
      });
    },
    [context]
  );

  return (
    <div
      className="list-item px-2"
      style={{ borderLeft: `5px ${icons.shippingRequired?.color} solid` }}
    >
      <div
        className="d-grid grid-column align-items-center"
        style={{
          gridTemplateColumns:
            "0.25fr 0.25fr 1.5fr 1fr 1.125fr 0.5fr 0.5fr 0.5fr 0.75fr auto auto",
        }}
      >
        <div className="grid-2x2">
          {icons.shippingRequired && (
            <FontAwesomeIcon
              icon={icons.shippingRequired.icon}
              title={icons.shippingRequired.label}
            />
          )}
          {icons.uniqueBarcodeItem && (
            <FontAwesomeIcon
              icon={icons.uniqueBarcodeItem.icon}
              title={`${dictionary.products.unique_barcode}: ${icons.uniqueBarcodeItem.label}`}
              color={icons.uniqueBarcodeItem.color}
            />
          )}
          {icons.domain && (
            <FontAwesomeIcon
              icon={icons.domain.icon}
              title={`${icons.domain.label}: ${
                item.pub
                  ? dictionary.messages.visible_to_sub_organization
                  : dictionary.messages.not_visible_to_sub_organization
              }`}
            />
          )}
        </div>
        <div className="text-center">
          {item.imageUrl && (
            <img
              style={{ maxWidth: "100%" }}
              src={item.imageUrl}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = NoAvailableImage;
              }}
              alt="Logo"
            />
          )}
        </div>
        <div>
          <label className="label-2">{dictionary.registries.name}</label>
          <label className="label-3">{item.name || "-"}</label>
        </div>
        <div>
          <label className="label-2">
            {dictionary.references.external_reference}
          </label>
          <label className="label-3">{item.externalReference || "-"}</label>
        </div>
        <div>
          <label className="label-2">
            {dictionary.dimensions.dimensions} (cm)
          </label>
          {item?.width && item?.length && item?.height ? (
            <label className="label-3">{`${item.width?.toFixed(
              2
            )} x ${item.height?.toFixed(2)} x ${item.length?.toFixed(
              2
            )}`}</label>
          ) : (
            "-"
          )}
        </div>
        <div>
          <label className="label-2">{dictionary.dimensions.weight} (kg)</label>
          <label className="label-3">{item.weight?.toFixed(2) || "-"}</label>
        </div>
        <div>
          <label className="label-2">{dictionary.dimensions.volume} (m3)</label>
          <label className="label-3">
            {item?.width && item?.length && item?.height ? (
              <label className="label-3">
                {((item.width * item.length * item.height) / 1000000).toFixed(
                  6
                )}
              </label>
            ) : (
              "-"
            )}
          </label>
        </div>
        <div>
          <label className="label-2">{dictionary.words.price} (€)</label>
          <label className="label-3">{item.price?.toFixed(2) || "-"}</label>
        </div>
        <div>
          <label className="label-2">{dictionary.products.barcode}</label>
          <label className="label-3">{item.barcode || "-"}</label>
        </div>
        <div>
          <label className="label-2">{dictionary.status.availables}</label>
          <label className="label-3">{item.totalAvailable || "-"}</label>
        </div>
        <div className="text-end">
          <Link
            className="button-1 button-1-light button-1-icon me-1"
            to={
              item.ownerId === accessTokenPayload.owner
                ? `/articles/edit/${item.id}`
                : `/articles/details/${item.id}`
            }
            title={
              item.ownerId === accessTokenPayload.owner
                ? dictionary.actions.edit
                : dictionary.actions.details
            }
          >
            <FontAwesomeIcon icon={faEye} />
          </Link>
          <button
            title={
              item.ownerId === accessTokenPayload.owner
                ? dictionary.actions.delete
                : dictionary.messages.not_deletable_you_must_be_the_owner
            }
            disabled={item.ownerId !== accessTokenPayload.owner}
            className="button-1 button-1-danger button-1-icon"
            onClick={() => {
              if (item.ownerId === accessTokenPayload.owner) {
                openModal(
                  "delete",
                  [item.id],
                  "sm",
                  "",
                  dictionary.actions.delete
                );
              }
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </div>
    </div>
  );
});
